import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  FormDatePicker,
  FormTextField,
  GridCardContent,
} from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  IDownloadReturnReport,
  IPaginateApiResponse,
  IReturn,
  IReturnRequest,
} from "model";
import { IGetForkliftPerformance } from "model/ForkLiftPerformance";
import moment, { Moment } from "moment";
import { useState } from "react";
import { DownloadActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { ReportApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

const ReturnReport = () => {
  const dispatch = useAppDispatch();

  const [request, setRequest] = useState<IReturnRequest>({
    type: "",
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
  });

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const { loading, result } = usePaginationRequest<IReturnRequest, IReturn[]>(
    request,
    ReportApiService.getReturnReport
  );

  const downloadReturnReport = async () => {
    const params: IDownloadReturnReport = {
      ...request,
      fileName: EXPORT_FILE_NAME.returnReport,
      pageSize: (result as IPaginateApiResponse<IReturn[]>).totalRecords,
      pageNumber: FIRST_PAGE,
    };
    dispatch(DownloadActions.downloadReturnReport.request(params));
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader
        request={request}
        onSubmit={setRequest}
        onDownload={downloadReturnReport}
      />
      <TableSection
        loading={loading}
        result={result}
        request={request}
        onSearch={handleSearch}
        onChangePage={handleChangePage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

const DetailHeader = (props: {
  request: IGetForkliftPerformance;
  onSubmit(values: IReturnRequest): void;
  onDownload(): void;
}) => {
  const { request, onSubmit, onDownload } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const initialValues = {
    fromDate: moment(request.fromDate, "MM-DD-YYYY"),
    toDate: moment(request.toDate, "MM-DD-YYYY"),
  };
  const formik = useFormik<{
    fromDate: Moment;
    toDate: Moment;
  }>({
    initialValues,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      const params = {
        type: values.type,
        pageNumber: 1,
        pageSize: request.pageSize,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      };
      onSubmit(params);
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.return)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<Download />}
            loading={downloading}
            variant="contained"
            onClick={onDownload}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.report.type)}
            id="type"
            select
          >
            <MenuItem key="C" value={"C"}>
              Hàng QR
            </MenuItem>
            <MenuItem key="P" value={"P"}>
              Hàng không QR
            </MenuItem>
          </FormTextField>
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  loading: boolean;
  result: IPaginateApiResponse<IReturn[]>;
  request: IGetForkliftPerformance;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
  onSearch(searchText: string): void;
}) => {
  const { loading, result, onChangePage, onPageSizeChange, onSearch } = props;

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "returnDate",
      headerName: I18nTranslator(translations.report.returnDate),
      size: "small",
      renderCell: ({ value }) => {
        return (
          <Typography>
            {value ? TimeHelper.formatDate(value, "DD/MM/YYYY HH:mm") : ""}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "actualDeliveryDate",
      headerName: I18nTranslator(translations.report.deliveryDate),
      size: "small",
      renderCell: ({ value }) => {
        return (
          <Typography>{value ? TimeHelper.formatDate(value) : ""}</Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "deliveryTicketCode",
      headerName: I18nTranslator(translations.report.DOImportCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "companyCode",
      headerName: I18nTranslator(translations.report.companyCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "companyName",
      headerName: I18nTranslator(translations.report.companyName),
      size: "full",
      align: "left",
    }),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "full",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCodeOld",
      headerName: I18nTranslator(translations.report.productCodeOld),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "cartonCode",
      headerName: I18nTranslator(translations.report.cartonCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "encryptedProductBarcode",
      headerName: I18nTranslator(translations.report.encryptedProductBarcode),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "returnQty",
      headerName: I18nTranslator(translations.report.exportBy),
      align: "right",
      size: "small",
    }),
    TableHelper.createCell({
      field: "userName",
      headerName: I18nTranslator(translations.report.returnBy),
      size: "small",
    }),
    TableHelper.createCell({
      field: "returnReason",
      headerName: I18nTranslator(translations.report.reason),
      size: "small",
    }),
    TableHelper.createCell({
      field: "note",
      headerName: I18nTranslator(translations.report.note),
      size: "full",
    }),
  ];

  return (
    <AppTable<IReturn>
      loading={loading}
      data={result}
      columns={COLUMNS}
      onSearch={onSearch}
      searchPlaceholder={"Tìm kiếm"}
      onChangePage={onChangePage}
      hideHeader
      removeActionColumn
      defaultHeaderTable={170}
      onPageSizeChange={onPageSizeChange}
    />
  );
};

export default ReturnReport;
